import { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { IndianRupee, ShoppingBag, Users as UsersIcon, Download, Filter, Plus, Mail, Phone, MapPin, CheckCircle, PlusCircle, ArrowRight } from 'lucide-react';
import Loading from '../../components/Loading';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

export default function DashboardOverview() {
    const { user } = useAuth();
    const toast = useToast();

    // Storefront admin state
    const [allOrders, setAllOrders] = useState([]);
    const [allUsers, setAllUsers] = useState([]);

    // Franchise admin state
    const [franchiseStats, setFranchiseStats] = useState(null);
    const [isAppointModalOpen, setIsAppointModalOpen] = useState(false);
    const [appointForm, setAppointForm] = useState({ branchName: '', fullName: '', email: '', phone: '' });
    const [appointLoading, setAppointLoading] = useState(false);
    const [appointedBranchUrl, setAppointedBranchUrl] = useState('');

    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            if (user?.role === 'franchise_admin') {
                const [statsRes] = await Promise.all([
                    api.get('/api/franchises/stats')
                ]);
                setFranchiseStats(statsRes.data);
            } else {
                const [ordersRes, usersRes] = await Promise.all([
                    api.get('/api/orders/all'),
                    api.get('/api/auth/admin/users')
                ]);
                setAllOrders(ordersRes.data);
                setAllUsers(usersRes.data);
            }
        } catch (error) {
            console.error('Failed to fetch dashboard data', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) {
            fetchDashboardData();
        }
    }, [user]);

    const handleAppointSubmit = async (e) => {
        e.preventDefault();
        setAppointLoading(true);
        try {
            const res = await api.post('/api/franchises/appoint', appointForm);
            const branchUrl = `${window.location.origin}/branch/${res.data.branch.slug}`;
            setAppointedBranchUrl(branchUrl);
            toast.success('Restaurant admin appointed successfully!');
            setAppointForm({ branchName: '', fullName: '', email: '', phone: '' });
            fetchDashboardData(); // Reload stats and branch list
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to appoint restaurant admin');
        } finally {
            setAppointLoading(false);
        }
    };

    const filteredData = useMemo(() => {
        if (user?.role === 'franchise_admin') return null;
        let filteredOrders = [...allOrders];

        if (dateRange.start) {
            filteredOrders = filteredOrders.filter(o => 
                new Date(o.created_at) >= new Date(dateRange.start)
            );
        }
        if (dateRange.end) {
            const endDate = new Date(dateRange.end);
            endDate.setHours(23, 59, 59, 999);
            filteredOrders = filteredOrders.filter(o => 
                new Date(o.created_at) <= endDate
            );
        }

        const stats = {
            totalRevenue: filteredOrders
                .filter(o => o.payment_status === 'paid')
                .reduce((sum, o) => sum + parseFloat(o.final_total_amount || o.total_amount || 0), 0),
            totalOrders: filteredOrders.length,
            totalUsers: allUsers.length
        };

        return { stats, orders: filteredOrders };
    }, [allOrders, allUsers, dateRange, user]);

    const exportToCSV = () => {
        if (user?.role === 'franchise_admin') return;
        const headers = ['Order ID', 'Customer', 'Amount', 'Status', 'Date'];
        const rows = filteredData.orders.map(o => [
            o.id,
            o.customer_name,
            parseFloat(o.final_total_amount || o.total_amount || 0).toFixed(2),
            o.status,
            new Date(o.created_at).toLocaleDateString()
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `orders_report_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) return <Loading mode="page" message="Loading dashboard statistics..." />;

    // Render Franchise Admin Dashboard
    if (user?.role === 'franchise_admin') {
        const stats = franchiseStats || {
            totalBranches: 0,
            totalSales: 0,
            totalOrders: 0,
            branchBreakdown: [],
            recentOrders: []
        };

        return (
            <div className="space-y-8 pb-20">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 tracking-tight">Franchise Consolidated Dashboard</h1>
                        <p className="text-slate-500 text-sm mt-1">Aggregated statistics and branch configurations across your brand portfolio.</p>
                    </div>
                    <button
                        onClick={() => { setAppointedBranchUrl(''); setIsAppointModalOpen(true); }}
                        className="btn-primary inline-flex items-center space-x-2 shadow-lg shadow-primary-500/20"
                    >
                        <PlusCircle className="w-4 h-4" />
                        <span>Appoint Branch Admin</span>
                    </button>
                </div>

                {/* Aggregate Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm flex items-center space-x-5 hover:scale-[1.01] transition-transform">
                        <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <IndianRupee className="w-8 h-8" />
                        </div>
                        <div>
                            <p className="text-slate-400 font-bold text-xs uppercase tracking-wider">Consolidated Sales</p>
                            <p className="text-2xl font-black text-slate-950 mt-1">₹{stats.totalSales.toFixed(2)}</p>
                        </div>
                    </div>
                    <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm flex items-center space-x-5 hover:scale-[1.01] transition-transform">
                        <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <ShoppingBag className="w-8 h-8" />
                        </div>
                        <div>
                            <p className="text-slate-400 font-bold text-xs uppercase tracking-wider">Total Brand Orders</p>
                            <p className="text-2xl font-black text-slate-950 mt-1">{stats.totalOrders}</p>
                        </div>
                    </div>
                    <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm flex items-center space-x-5 hover:scale-[1.01] transition-transform">
                        <div className="w-14 h-14 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <MapPin className="w-8 h-8" />
                        </div>
                        <div>
                            <p className="text-slate-400 font-bold text-xs uppercase tracking-wider">Active Branch Locations</p>
                            <p className="text-2xl font-black text-slate-950 mt-1">{stats.totalBranches}</p>
                        </div>
                    </div>
                </div>

                {/* Branches breakdown */}
                <div>
                    <h2 className="text-xl font-bold text-slate-900 mb-4">Branch Directory & Performance</h2>
                    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead className="bg-slate-55/40">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Branch Details</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Slug</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Appointed Admin</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Orders count</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Total Sales</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-slate-100">
                                    {stats.branchBreakdown.map(branch => (
                                        <tr key={branch.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="font-bold text-slate-900">{branch.name}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-slate-500 text-sm font-mono">{branch.slug}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-semibold text-slate-700">{branch.admin_name || 'Admin Pending'}</div>
                                                <div className="text-xs text-slate-400 font-mono mt-0.5">{branch.admin_email || ''}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-slate-500 font-medium text-sm">{branch.order_count} orders</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-slate-900 font-bold text-sm">₹{parseFloat(branch.sales || 0).toFixed(2)}</td>
                                        </tr>
                                    ))}
                                    {stats.branchBreakdown.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-12 text-center text-slate-400 italic">
                                                No branches appointed yet. Click "Appoint Branch Admin" to add your first restaurant location.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Recent consolidated orders */}
                <div>
                    <h2 className="text-xl font-bold text-slate-900 mb-4">Consolidated Recent Transactions (Read-Only)</h2>
                    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead className="bg-slate-55/40">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Order ID</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Branch Location</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Amount</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Date</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Payment Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-slate-100">
                                    {stats.recentOrders.map(order => (
                                        <tr key={order.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-slate-500 font-mono text-sm">#{order.id}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-slate-800 font-semibold text-sm">{order.branch_name}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-slate-900 font-bold text-sm">₹{parseFloat(order.final_total_amount || 0).toFixed(2)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-slate-400 text-xs">{new Date(order.created_at).toLocaleString()}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                                                    order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                                    order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                    {order.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                                                    order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                                                }`}>
                                                    {order.payment_status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    {stats.recentOrders.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-12 text-center text-slate-400 italic">
                                                No transactions recorded across brand branches yet.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Appoint Modal */}
                {isAppointModalOpen && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
                        <div className="bg-white rounded-3xl max-w-lg w-full p-8 shadow-2xl border border-slate-100 relative animate-scale-up">
                            {appointedBranchUrl ? (
                                <div className="space-y-6 text-center">
                                    <div className="w-16 h-16 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center mx-auto">
                                        <CheckCircle className="w-8 h-8" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-slate-900">Branch & Admin Created!</h3>
                                        <p className="text-slate-500 text-sm mt-1">An invitation email has been sent. Copy the branch storefront URL below.</p>
                                    </div>
                                    <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-between gap-3">
                                        <span className="text-xs text-slate-600 select-all break-all text-left font-mono">{appointedBranchUrl}</span>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(appointedBranchUrl);
                                                toast.success('URL copied to clipboard!');
                                            }}
                                            className="bg-primary-50 hover:bg-primary-100 text-primary-600 text-xs px-3 py-1.5 rounded-lg font-bold transition-colors flex-shrink-0"
                                        >
                                            Copy
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setAppointedBranchUrl('');
                                            setIsAppointModalOpen(false);
                                        }}
                                        className="w-full bg-slate-950 hover:bg-slate-900 text-white py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors"
                                    >
                                        Close Workspace
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <h3 className="text-2xl font-black text-slate-900 mb-2">Appoint Restaurant Branch Admin</h3>
                                    <p className="text-slate-500 text-sm mb-6">Initialize a new franchise branch and appoint an administrator. They will receive an invitation email to set their login credentials.</p>

                                    <form onSubmit={handleAppointSubmit} className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Branch Name (e.g. Adyar, T-Nagar)</label>
                                            <input
                                                type="text"
                                                required
                                                placeholder="Enter branch name"
                                                value={appointForm.branchName}
                                                onChange={(e) => setAppointForm({ ...appointForm, branchName: e.target.value })}
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Admin Full Name</label>
                                            <input
                                                type="text"
                                                required
                                                placeholder="Enter admin full name"
                                                value={appointForm.fullName}
                                                onChange={(e) => setAppointForm({ ...appointForm, fullName: e.target.value })}
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Admin Email Address</label>
                                            <input
                                                type="email"
                                                required
                                                placeholder="admin@franchisebranch.com"
                                                value={appointForm.email}
                                                onChange={(e) => setAppointForm({ ...appointForm, email: e.target.value })}
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Admin Contact Phone</label>
                                            <input
                                                type="tel"
                                                placeholder="+91 98765 43210"
                                                value={appointForm.phone}
                                                onChange={(e) => setAppointForm({ ...appointForm, phone: e.target.value })}
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                                            />
                                        </div>

                                        <div className="flex gap-4 pt-4 border-t border-slate-100">
                                            <button
                                                type="button"
                                                onClick={() => setIsAppointModalOpen(false)}
                                                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={appointLoading}
                                                className="flex-1 btn-primary py-3.5 text-xs font-bold uppercase tracking-wider flex items-center justify-center space-x-2"
                                            >
                                                {appointLoading ? <span>Sending...</span> : <span>Appoint Admin</span>}
                                            </button>
                                        </div>
                                    </form>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Render Storefront Admin Dashboard
    const { stats, orders } = filteredData;

    return (
        <div className="space-y-6 pb-20">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
                
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center space-x-2 bg-white px-3 py-2 rounded-lg border border-gray-200">
                        <Filter className="w-4 h-4 text-gray-400" />
                        <input 
                            type="date" 
                            className="text-sm outline-none border-none focus:ring-0"
                            value={dateRange.start}
                            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                        />
                        <span className="text-gray-400 text-sm">to</span>
                        <input 
                            type="date" 
                            className="text-sm outline-none border-none focus:ring-0"
                            value={dateRange.end}
                            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                        />
                    </div>
                    <button 
                        onClick={exportToCSV}
                        className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
                    >
                        <Download className="w-4 h-4" />
                        <span>Export CSV</span>
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="card p-6 flex items-center space-x-4 transition-transform hover:scale-[1.02]">
                    <div className="p-3 bg-green-100 text-green-600 rounded-full">
                        <IndianRupee className="w-8 h-8" />
                    </div>
                    <div>
                        <p className="text-gray-500 font-medium">Revenue (Filter)</p>
                        <p className="text-2xl font-bold mt-1">₹{stats.totalRevenue.toFixed(2)}</p>
                    </div>
                </div>
                <div className="card p-6 flex items-center space-x-4 transition-transform hover:scale-[1.02]">
                    <div className="p-3 bg-blue-100 text-blue-600 rounded-full">
                        <ShoppingBag className="w-8 h-8" />
                    </div>
                    <div>
                        <p className="text-gray-500 font-medium">Orders (Filter)</p>
                        <p className="text-2xl font-bold mt-1">{stats.totalOrders}</p>
                    </div>
                </div>
                <div className="card p-6 flex items-center space-x-4 transition-transform hover:scale-[1.02]">
                    <div className="p-3 bg-purple-100 text-purple-600 rounded-full">
                        <UsersIcon className="w-8 h-8" />
                    </div>
                    <div>
                        <p className="text-gray-500 font-medium">Total Users</p>
                        <p className="text-2xl font-bold mt-1">{stats.totalUsers}</p>
                    </div>
                </div>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">
                {dateRange.start || dateRange.end ? 'Filtered Orders' : 'Recent Orders'}
            </h2>
            <div className="card overflow-x-auto shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {orders.slice(0, dateRange.start || dateRange.end ? undefined : 10).map(order => (
                            <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">#{order.id}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.customer_name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">₹{parseFloat(order.final_total_amount || order.total_amount || 0).toFixed(2)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{new Date(order.created_at).toLocaleDateString()}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase transition-colors ${order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-blue-100 text-blue-700'
                                        }`}>
                                        {order.status}
                                    </span>
                                </td>
                            </tr>
                        ))}
                        {orders.length === 0 && (
                            <tr>
                                <td colSpan="5" className="px-6 py-10 text-center text-gray-500 italic">
                                    No orders found for the selected period.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
